import { PDFDocument, ParseSpeeds } from "pdf-lib";
import { showErrorToast } from "./toast";
import { CHUNK_SIZE } from "../static/pdf.js";

const loadPDFsAndMerge = async (files) => {
  const fileNames = [];
  const pdfDoc = await PDFDocument.create();

  for (const file of files) {
    if (!file.name.endsWith(".pdf") && file.type !== "application/pdf") {
      showErrorToast("Invalid file format. Please select a PDF file.");
    } else {
      fileNames.push(file.name);

      const pdfBytes = await file.arrayBuffer();
      const pdf = await PDFDocument.load(pdfBytes);
      const indices = pdf.getPageIndices();

      const pages = await pdfDoc.copyPages(pdf, indices);
      pages.forEach((page) => pdfDoc.addPage(page));
    }
  }

  const mergedPdf = await pdfDoc.save();

  return { mergedPdf, fileNames };
};

export const splitPDF = async (originalFile, pageNumber) => {
  if (
    !originalFile.name.endsWith(".pdf") &&
    originalFile.type !== "application/pdf"
  ) {
    return showErrorToast("Invalid file format. Please select a PDF file.");
  }

  const pdfBytes = await originalFile.arrayBuffer();
  const pdfDoc = await PDFDocument.load(pdfBytes);
  const totalPages = pdfDoc.getPageCount();

  if (pageNumber < 1 || pageNumber >= totalPages) {
    return showErrorToast("Invalid position to split the PDF at.");
  }

  const pdfFirstHalf = await PDFDocument.create();
  const pdfSecondHalf = await PDFDocument.create();

  const splitFirstHalf = await pdfFirstHalf.copyPages(
    pdfDoc,
    Array.from({ length: pageNumber }, (_, i) => i)
  );
  const splitSecondHalf = await pdfSecondHalf.copyPages(
    pdfDoc,
    Array.from({ length: totalPages - pageNumber }, (_, i) => i + pageNumber)
  );

  splitFirstHalf.forEach((page) => pdfFirstHalf.addPage(page));
  splitSecondHalf.forEach((page) => pdfSecondHalf.addPage(page));

  const [splitFirstHalfPDF, splitSecondHalfPDF] = await Promise.all([
    pdfFirstHalf.save(),
    pdfSecondHalf.save(),
  ]);

  const splitFirstHalfPDFBlob = new Blob([splitFirstHalfPDF], {
    type: "application/pdf",
  });
  const splitSecondHalfPDFBlob = new Blob([splitSecondHalfPDF], {
    type: "application/pdf",
  });

  const splitFirstHalfPDFUrl = new File(
    [splitFirstHalfPDFBlob],
    "output1.pdf",
    { type: "application/pdf" }
  );
  const splitSecondHalfPDFUrl = new File(
    [splitSecondHalfPDFBlob],
    "output2.pdf",
    { type: "application/pdf" }
  );

  return [splitFirstHalfPDFUrl, splitSecondHalfPDFUrl];
};

export const mergeMultiplePdfs = async (files) => {
  const { mergedPdf, fileNames } = await loadPDFsAndMerge(files);

  const pdfBlob = new Blob([mergedPdf], { type: "application/pdf" });
  const pdfUrl = URL.createObjectURL(pdfBlob);

  return { pdfUrl, pdfBlob, fileNames };
};

export const getTotalPagesInPDF = async (pdf) => {
  const pdfBytes = await pdf.arrayBuffer();
  const pdfDoc = await PDFDocument.load(pdfBytes);
  const totalPages = pdfDoc.getPageCount();
  return totalPages;
};

export const bytesToString = (bytes) => {
  let out = "";
  for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
    out += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK_SIZE));
  }
  return out;
};

export const stringToBytes = (text) => {
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i += 1) bytes[i] = text.charCodeAt(i);
  return bytes;
};

export const loadPdfWithTitle = async (pdf, title) => {
  try {
    const pdfDoc = await PDFDocument.load(await pdf.arrayBuffer(), {
      updateMetadata: false,
      parseSpeed: ParseSpeeds.Fastest,
    });
    pdfDoc.setTitle(title, { showInWindowTitleBar: true });
    const bytes = await pdfDoc.save({ objectsPerTick: Infinity });
    return new Blob([bytes], { type: "application/pdf" });
  } catch (error) {
    return pdf;
  }
};
